import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useNumericInput from "../../hooks/useNumericInput";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import CartItemsinCheckout from "../canvas/CartItemsinCheckout";
import { useCart } from "../../context/CartContext";
import Summary from "./common/Summary";
import addressAPI from "../../api/addressApi";
import userAPI from "../../api/UserApi";
import { useCheckout } from "../../hooks/useCheckout";
import { showError, showSuccess } from "../../utils/toastUtils";
import PaymentService from "./gateways/PaymentServices";
import { useSiteConfig } from "../../context/SiteConfigContext";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import { Check, CreditCard } from "lucide-react";
import Breadcrump from "../../components/Breadcrump";

const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

function Checkout() {
  useNumericInput();
  useAlphabeticInput();
  const { cartItems, fetchCart, cartSummary } = useCart();
  const { config } = useSiteConfig();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState("contact");

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");

  // ---------- OTP STATE ----------
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  // Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const paymentMethods = [
    {
      code: "cod",
      name: "Cash on Delivery",
    },
    {
      code: "razorpay",
      name: "Pay via Razorpay (UPI, Cards, NetBanking)",
    },
  ];

  /****************** STATE DECLARATION *********/
  const auth = JSON.parse(localStorage.getItem("token") ?? "{}");

  const INITIAL_FORM_DATA = {
    email: auth.email || "", // UI me nahi dikhta, sirf logged-in user ke liye backend ko jata hai
    phone: sessionStorage.getItem("phoneNum") || "",
    firstName: "",
    lastName: "",
    address: {
      addressType: "home",
      addressLine1: "",
      addressLine2: "",
      pinCode: "",
      district: "",
      state: "",
      country: "",
    },
    paymentMethod: "razorpay",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const { placeOrder } = useCheckout(cartItems, formData);

  /****************** STATE CHANGE *********/
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedAddress("");

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  /**************** CONTACT VALIDATION **************/
  const phoneRegex = /^[6-9]\d{9}$/;

  const isAddressComplete =
    String(formData.firstName || "").trim() &&
    String(formData.address.addressType || "").trim() &&
    String(formData.address.addressLine1 || "").trim() &&
    String(formData.address.pinCode || "").trim() &&
    String(formData.address.district || "").trim() &&
    String(formData.address.state || "").trim() &&
    String(formData.address.country || "").trim();

  const validateContact = () => {
    const newErrors = {};

    if (!formData.phone) {
      newErrors.phone = "Enter Phone Number";
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Phone must be of 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors = {};

    if (!otp) {
      newErrors.otp = "Please Enter OTP";
    } else if (otp.length !== OTP_LENGTH) {
      newErrors.otp = `OTP must be ${OTP_LENGTH} digits`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**************** SESSION STORAGE **************/
  const saveSessionData = () => {
    const { phone } = formData;

    phone
      ? sessionStorage.setItem("phoneNum", phone)
      : sessionStorage.removeItem("phoneNum");
  };

  /**************** OTP HANDLERS **************/
  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!validateContact()) return;

    try {
      setLoading(true);
      setErrors({});

      const res = await userAPI.sendOtp({
        phone: formData.phone,
      });
      console.log("sendOtp response:", res);

      if (res.code === 200) {
        setOtpSent(true);
        setOtp("");
        setResendTimer(RESEND_SECONDS);
      } else {
        showError(res.message || "OTP bhejne mein problem aayi");
      }
    } catch (err) {
      console.error("sendOtp error:", err);
      handleApiValidationError(err, setErrors);
      showError(err?.response?.data?.message || err.message || "OTP failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!validateOtp()) return;

    try {
      setLoading(true);
      setErrors({});

      const res = await userAPI.verifyOtp({
        phone: formData.phone,
        otp: otp,

        // API requires these fields
        firstName: "",
        lastName: "",
        email: formData.email,
      });

      if (res.code === 200) {
        setOtpVerified(true);
        setErrors({});

        saveSessionData();

        setActive("address");

        getAllMyAddress();
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  /**************** FETCH ADDRESS **************/
  const getAllMyAddress = async () => {
    try {
      const res = await addressAPI.fetchAllAddress({
        email: formData.email,
        phone: formData.phone,
      });
      if (res.code === 200) {
        setSavedAddresses(res.data);

        setFormData((prev) => ({
          ...prev,
          firstName: res.data.firstName || "",
          lastName: res.data.lastName || "",
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  /**************** SELECT SAVED ADDRESS **************/
  const handleSavedAddressSelect = (address) => {
    setSelectedAddress(address._id);

    setFormData((prev) => ({
      ...prev,
      firstName: address.firstName || "",
      lastName: address.lastName || "",
      address: {
        addressType: address.addressType || "home",
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        pinCode: address.pinCode || "",
        district: address.district || "",
        state: address.state || "",
        country: address.country || "",
      },
    }));
  };

  /****************** PINCODE DATA *********/
  useEffect(() => {
    if (formData.address?.pinCode?.length === 6) {
      fetchPincodeDetails(formData.address?.pinCode);
    }
  }, [formData.address?.pinCode]);

  const fetchPincodeDetails = async (pincode) => {
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();

      if (data[0].Status === "Success") {
        const postOffice = data[0].PostOffice[0];
        setFormData((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            state: postOffice.State,
            district: postOffice.District,
            country: postOffice.Country,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching pincode:", error);
    }
  };

  /****************** ADDRESS VALIDATION *********/
  const addressvalidate = () => {
    const requiredFields = [
      "firstName",
      "addressType",
      "addressLine1",
      "pinCode",
      "district",
      "state",
      "country",
    ];
    const errors = {};

    requiredFields.forEach((field) => {
      if (field === "firstName") {
        if (!formData.firstName) errors[field] = `Please Enter First Name`;
      } else {
        if (!formData.address[field]) errors[field] = `Please Enter ${field}`;
      }
    });

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /****************** PLACE ORDER *********/
  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    // Safety guard: OTP verify hue bina order nahi
    if (!otpVerified) {
      showError("Please verify your contact details with OTP first");
      setActive("contact");
      return;
    }

    setLoading(true);

    try {
      if (!addressvalidate()) {
        setLoading(false);
        return;
      }

      const action = await placeOrder();
      if (!action) throw new Error("Something went wrong");

      // ---------- COD SUCCESS ----------
      if (action.type === "COD_SUCCESS") {
        const { orderId } = action.payload;

        showSuccess(action.message);

        sessionStorage.setItem("orderId", orderId);
        sessionStorage.removeItem("phoneNum");

        await fetchCart(); // cart refresh/clear

        navigate("/order-confirmation");
        return;
      }

      // ---------- ONLINE PAYMENT ----------
      if (action.type === "PAYMENT_STARTED") {
        const { orderId, paymentMethod, gatewayOrderId, amount, currency } =
          action.payload;

        PaymentService.pay(paymentMethod.toLowerCase(), {
          gatewayOrderId,
          orderId,
          amount,
          currency,
          formData,
          navigate,
          config,
          fetchCart,
        });
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrump title="Checkout" />

      <div className="luxury-checkout-page py-5">
        <div className="container">
          <div className="row g-5">

            {/* LEFT SIDEFORM DETAILS PANEL */}
            <div className="col-xl-8">
              <div className="luxury-checkout-accordions">

                {/* SEGMENT 1: CONTACT BLOCK (with OTP) */}
                <div className={`luxury-step-card mb-4 ${active === "contact" ? "is-active" : "is-collapsed"}`}>
                  <div
                    className="step-header-trigger d-flex justify-content-between align-items-center"
                    onClick={() => setActive("contact")}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="step-numeric-bullet">1</span>
                      <h4 className="step-title-text mb-0">Contact Details</h4>
                    </div>
                    {active === "address" && otpVerified && (
                      <span className="step-summary-badge d-none d-md-inline-block">
                        +91 {formData.phone}
                      </span>
                    )}
                  </div>

                  <div className={`step-body-content collapse-engine ${active === "contact" ? "open-panel" : ""}`}>
                    <form onSubmit={otpSent ? handleVerifyOtp : handleSendOtp} className="pt-4">
                      <div className="row g-4">
                        {/* PHONE */}
                        <div className="col-md-6">
                          <div className="editorial-input-group">
                            <input
                              className="editorial-input-field only-numeric"
                              type="text"
                              name="phone"
                              placeholder=" "
                              maxLength={10}
                              value={formData.phone}
                              onChange={handleChange}
                              disabled={otpSent || otpVerified}
                            />
                            <label className="editorial-input-label">Phone Number *</label>
                            {errors?.phone && <span className="field-error-msg">{errors.phone}</span>}
                          </div>
                        </div>

                        {/* OTP INPUT — sirf OTP send hone ke baad dikhega */}
                        {otpSent && !otpVerified && (
                          <div className="col-md-6">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-numeric"
                                type="text"
                                placeholder=" "
                                value={otp}
                                maxLength={OTP_LENGTH}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                onChange={(e) => {
                                  const value = e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, OTP_LENGTH);

                                  setOtp(value);
                                  setErrors((prev) => ({ ...prev, otp: "" }));
                                }}
                              />
                              <label className="editorial-input-label">Enter OTP *</label>
                              {errors?.otp && <span className="field-error-msg">{errors.otp}</span>}
                            </div>

                            <div className="mt-2">
                              {resendTimer > 0 ? (
                                <small className="text-muted">Resend OTP in {resendTimer}s</small>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-muted text-decoration-none"
                                  onClick={handleSendOtp}
                                  disabled={loading}
                                >
                                  Resend OTP
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {otpVerified && (
                          <div className="col-12">
                            <span className="text-success fw-bold d-inline-flex align-items-center gap-2">
                              <Check size={16} strokeWidth={3} /> Contact details verified
                            </span>
                          </div>
                        )}

                        <div className="col-12 text-end">
                          {otpVerified ? (
                            <button
                              type="button"
                              className="luxury-action-btn-dark px-5 py-3"
                              onClick={() => {
                                setActive("address");
                                getAllMyAddress();
                              }}
                            >
                              CONTINUE TO SHIPPING
                            </button>
                          ) : (
                            <button
                              type="submit"
                              className="luxury-action-btn-dark px-5 py-3"
                              disabled={loading}
                            >
                              {otpSent
                                ? loading ? "VERIFYING..." : "VERIFY OTP"
                                : loading ? "SENDING OTP..." : "SEND OTP"}
                            </button>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* SEGMENT 2: SHIPPING ADDRESS BLOCK */}
                <div className={`luxury-step-card ${active === "address" ? "is-active" : "is-collapsed"}`}>
                  <div
                    className="step-header-trigger d-flex justify-content-between align-items-center"
                    onClick={() => {
                      if (!otpVerified) return;
                      setActive("address");
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span className="step-numeric-bullet">2</span>
                      <h4 className="step-title-text mb-0">Shipping Address</h4>
                    </div>
                  </div>

                  <div className={`step-body-content collapse-engine ${active === "address" ? "open-panel" : ""}`}>
                    <div className="pt-4">
                      {/* Saved Addresses Matrix Sub-grid */}
                      {savedAddresses.length > 0 && (
                        <div className="luxury-saved-addresses-grid mb-5">
                          <p className="saved-address-heading mb-3">Select from Saved Addresses</p>
                          <div className="row g-3">
                            {savedAddresses.map((address) => (
                              <div className="col-md-6" key={address._id}>
                                <div
                                  className={`luxury-address-select-card position-relative ${selectedAddress === address._id ? "selected-active" : ""}`}
                                  onClick={() => handleSavedAddressSelect(address)}
                                >
                                  <div className="d-flex justify-content-between align-items-start">
                                    <span className="address-type-tag">{address.addressType}</span>
                                    {selectedAddress === address._id && (
                                      <span className="selected-checkmark-bullet"><Check size={14} strokeWidth={3} /></span>
                                    )}
                                  </div>
                                  <h6 className="address-client-name mt-3 mb-1">{address.firstName} {address.lastName}</h6>
                                  <p className="address-details-body mb-0">
                                    {address.addressLine1}, {address.addressLine2 && `${address.addressLine2}, `}
                                    {address.district}, {address.state} - <strong>{address.pinCode}</strong>
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Manual Interactive Address Fields Form */}
                      <div className="luxury-manual-address-form">
                        <p className="saved-address-heading mb-4">Or Enter New Delivery Address</p>
                        <div className="row g-4">
                          <div className="col-md-6">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-alphabets"
                                type="text"
                                name="firstName"
                                placeholder=" "
                                value={formData.firstName}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">First Name *</label>
                              {errors?.firstName && <span className="field-error-msg">{errors.firstName}</span>}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-alphabets"
                                type="text"
                                name="lastName"
                                placeholder=" "
                                value={formData.lastName}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">Last Name</label>
                            </div>
                          </div>

                          <div className="col-12">
                            <label className="editorial-type-selector-label mb-2 d-block">Address Type *</label>
                            <div className="d-flex gap-3 luxury-radio-tabs-group">
                              {["home", "work", "other"].map((type) => (
                                <label className={`luxury-radio-tab-item ${formData.address.addressType === type ? "tab-checked" : ""}`} key={type}>
                                  <input
                                    type="radio"
                                    value={type}
                                    name="address.addressType"
                                    checked={formData.address.addressType === type}
                                    onChange={handleChange}
                                    className="d-none"
                                  />
                                  <span className="text-uppercase text-sm fw-bold">{type}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field"
                                type="text"
                                name="address.addressLine1"
                                placeholder=" "
                                value={formData.address.addressLine1}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">Flat, House no., Building, Company, Apartment *</label>
                              {errors?.addressLine1 && <span className="field-error-msg">{errors.addressLine1}</span>}
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field"
                                type="text"
                                name="address.addressLine2"
                                placeholder=" "
                                value={formData.address.addressLine2}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">Area, Colony, Street, Sector, Village (Optional)</label>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-numeric"
                                type="text"
                                name="address.pinCode"
                                maxLength={6}
                                placeholder=" "
                                value={formData.address.pinCode}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">Pincode *</label>
                              {errors?.pinCode && <span className="field-error-msg">{errors.pinCode}</span>}
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-alphabets"
                                type="text"
                                name="address.district"
                                placeholder=" "
                                value={formData.address.district}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">District *</label>
                              {errors?.district && <span className="field-error-msg">{errors.district}</span>}
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-alphabets"
                                type="text"
                                name="address.state"
                                placeholder=" "
                                value={formData.address.state}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">State *</label>
                              {errors?.state && <span className="field-error-msg">{errors.state}</span>}
                            </div>
                          </div>

                          <div className="col-12">
                            <div className="editorial-input-group">
                              <input
                                className="editorial-input-field only-alphabets"
                                type="text"
                                name="address.country"
                                placeholder=" "
                                value={formData.address.country}
                                onChange={handleChange}
                              />
                              <label className="editorial-input-label">Country *</label>
                              {errors?.country && <span className="field-error-msg">{errors.country}</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* RIGHT SIDEBAR ORDER DETAILS PANEL */}
            <div className="col-xl-4">
              <div className="luxury-checkout-sidebar-panel">
                <h5 className="checkout-sidebar-title mb-4">In Your Bag</h5>

                {cartItems && cartItems.length > 0 && (
                  <div className="checkout-items-embedded-list mb-4">
                    {cartItems.map((item) => (
                      <CartItemsinCheckout item={item} key={item._id} />
                    ))}
                  </div>
                )}

                <div className="summary-card-inner-box mb-4">
                  <Summary cartItems={cartItems} cartSummary={cartSummary} />
                </div>

                <hr className="my-4" />

                <h5 className="checkout-sidebar-title mb-3">Payment Method</h5>
                <div className="luxury-payment-methods-stack mb-4">
                  {paymentMethods.map((method) => (
                    <label className="luxury-payment-method-card d-flex align-items-center gap-3" key={method.code}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method.code}
                        checked={formData.paymentMethod === method.code}
                        onChange={handleChange}
                        className="luxury-native-radio"
                      />
                      <span className="payment-icon-frame"><CreditCard size={16} /></span>
                      <span className="payment-method-name-text">{method.name}</span>
                    </label>
                  ))}
                </div>

                <button
                  type="button"
                  className="luxury-place-order-cta-btn w-100 d-flex align-items-center justify-content-center"
                  onClick={handlePlaceOrder}
                  disabled={loading || !otpVerified || !isAddressComplete}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      PROCESSING PAYMENT...
                    </>
                  ) : (
                    "PLACE ORDER NOW"
                  )}
                </button>
                {!otpVerified && (
                  <small className="d-block text-center text-muted mt-2">
                    Verify your contact details with OTP to place the order
                  </small>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

export default Checkout;